import { Preferences } from '@capacitor/preferences';

export type TPushNotificationTarget = {
  type: 'chat_message';
  roomId: string;
  storeId: string;
  messageId?: string;
  profileId?: string;
  userId?: string;
  channelId?: string;
  receivedAt: string;
};

const PENDING_PUSH_TARGET_KEY = 'forstok.push.pendingTarget';
export const PUSH_PENDING_TARGET_EVENT = 'forstok:push-pending-target';

const parsePendingPushTarget = (value: string | null): TPushNotificationTarget | null => {
  if (!value) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value) as Partial<TPushNotificationTarget>;
    if (
      parsedValue.type !== 'chat_message' ||
      !parsedValue.roomId ||
      !parsedValue.storeId
    ) {
      return null;
    }

    return {
      type: parsedValue.type,
      roomId: parsedValue.roomId,
      storeId: parsedValue.storeId,
      messageId: parsedValue.messageId,
      profileId: parsedValue.profileId,
      userId: parsedValue.userId,
      channelId: parsedValue.channelId,
      receivedAt: parsedValue.receivedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
};

const cachePendingPushTarget = (value: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (value === null) {
    window.localStorage.removeItem(PENDING_PUSH_TARGET_KEY);
    return;
  }

  window.localStorage.setItem(PENDING_PUSH_TARGET_KEY, value);
};

const dispatchPendingPushTargetEvent = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(PUSH_PENDING_TARGET_EVENT));
};

export const getChatsPathForPushTarget = () => '/';

export const getPostAuthRedirectPath = async () => {
  const pendingTarget = await getStoredPendingPushTarget();

  if (!pendingTarget) {
    return '/';
  }

  return getChatsPathForPushTarget();
};

const getPayloadString = (payload: Record<string, unknown>, key: string) => {
  const value = payload[key];
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
};

export const parsePushNotificationTarget = (data: unknown): TPushNotificationTarget | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const payload = data as Record<string, unknown>;
  const type = getPayloadString(payload, 'type');
  const roomId = getPayloadString(payload, 'room_id');
  const storeId = getPayloadString(payload, 'store_id');
  const messageId = getPayloadString(payload, 'message_id');
  const profileId = getPayloadString(payload, 'profile_id');
  const userId = getPayloadString(payload, 'user_id');
  const channelId = getPayloadString(payload, 'channel_id');

  if (type !== 'chat_message' || !roomId || !storeId) {
    return null;
  }

  return {
    type,
    roomId,
    storeId,
    ...(messageId && { messageId }),
    ...(profileId && { profileId }),
    ...(userId && { userId }),
    ...(channelId && { channelId }),
    receivedAt: new Date().toISOString(),
  };
};

export const storePendingPushTarget = async (target: TPushNotificationTarget) => {
  const serializedTarget = JSON.stringify(target);

  cachePendingPushTarget(serializedTarget);
  await Preferences.set({
    key: PENDING_PUSH_TARGET_KEY,
    value: serializedTarget,
  });
  dispatchPendingPushTargetEvent();
};

export const getStoredPendingPushTarget = async () => {
  if (typeof window !== 'undefined') {
    const cachedTarget = parsePendingPushTarget(window.localStorage.getItem(PENDING_PUSH_TARGET_KEY));
    if (cachedTarget) {
      return cachedTarget;
    }
  }

  const pendingTarget = await Preferences.get({
    key: PENDING_PUSH_TARGET_KEY,
  });

  return parsePendingPushTarget(pendingTarget.value);
};

export const clearStoredPendingPushTarget = async () => {
  cachePendingPushTarget(null);
  await Preferences.remove({
    key: PENDING_PUSH_TARGET_KEY,
  });
  dispatchPendingPushTargetEvent();
};
